import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Login = () => {
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        // Cleanup timeout on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleGoogleLogin = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        try {
            setLoading(true);
            setError('');
            
            // Redirect to backend OAuth endpoint
            // Backend will handle PKCE flow and redirect back
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
            const redirectUrl = `${apiUrl}/v1/auth/google/start`;
            
            console.log('[Login] Redirecting to Google OAuth:', redirectUrl);
            
            // Set a timeout to reset loading state if redirect doesn't happen
            timeoutRef.current = setTimeout(() => {
                console.warn('[Login] Redirect timeout - resetting loading state');
                setLoading(false);
                setError('Redirect is taking longer than expected. Please check your connection and backend server.');
            }, 5000); // 5 second timeout
            
            // Use window.location.href for redirect (more reliable for user-initiated actions)
            window.location.href = redirectUrl;
        } catch (err) {
            console.error('[Login] Error initiating Google login:', err);
            setError('Failed to initiate Google login. Please try again.');
            setLoading(false);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        }
    };

    return (
        <div className="relative min-h-screen w-full overflow-hidden">
            {/* Background Video with Blur */}
            <div className="fixed inset-0">
                <video
                    src="/BackgroundVideo.mp4"
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ filter: 'blur(8px)' }}
                />
                {/* Dark overlay to improve contrast */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'radial-gradient(circle at center, transparent 0%, transparent 20%, rgba(0, 0, 0, 0.3) 60%, rgba(0, 0, 0, 0.6) 100%)'
                    }}
                ></div>
            </div>

            {/* Logo Header - Clickable - Matching Landing Page */}
            <div
                className="fixed flex items-center space-x-3 z-20"
                style={{ left: '10%', top: '1.5rem' }}
            >
                <button
                    onClick={() => navigate('/')}
                    className="flex items-center gap-3 text-white hover:opacity-80 transition-opacity duration-200"
                >
                    <img src="/logo.png" alt="mifamli logo" className="h-12 w-12" />
                    <span className="text-3xl font-bold drop-shadow-lg">mifamli</span>
                </button>
            </div>

            {/* Login Form Container */}
            <div className="relative z-10 min-h-screen flex items-center justify-center px-8 md:px-16 lg:px-24">
                <div
                    className="w-full max-w-xl backdrop-blur-md rounded-3xl shadow-2xl p-10 md:p-14 border border-white/30"
                    style={{
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.7) 0%, rgba(236, 72, 153, 0.7) 100%)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    }}
                >
                    {/* Logo and Title */}
                    <div className="text-center mb-10" style={{ paddingTop: '20px', paddingBottom: '20px' }}>
                        <div className="flex items-center justify-center space-x-4 mb-5">
                            <img src="/logo.png" alt="mifamli logo" className="h-14 w-14" />
                            <span className="text-4xl font-bold text-white drop-shadow-lg">mifamli</span>
                        </div>
                    </div>
                    <div className="text-center mb-10">
                        <h1 className="text-3xl font-bold text-white mb-3">Welcome</h1>
                        <p className="text-xl text-white/90">Sign in to access your family hub</p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-5 p-4 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white text-base">
                            {error}
                        </div>
                    )}

                    {/* Loading State */}
                    {loading && (
                        <div className="mb-5 p-4 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white text-base text-center">
                            Signing you in...
                        </div>
                    )}

                    {/* Google Login Button */}
                    <div className="mb-7 flex justify-center w-full">
                        <button
                            type="button"
                            onClick={handleGoogleLogin}
                            disabled={loading}
                            className="w-full max-w-[400px] flex items-center justify-center gap-4 px-10 py-5 bg-white border-2 border-gray-400 rounded-xl hover:bg-gray-50 hover:border-primary-blue hover:shadow-lg transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ position: 'relative', zIndex: 10 }}
                        >
                            <svg className="w-7 h-7" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            <span className="text-gray-800 font-semibold text-xl">Continue with Google</span>
                        </button>
                    </div>

                    {/* Terms and Privacy */}
                    <p className="text-sm text-white/80 text-center mt-7">
                        By continuing, you agree to mifamli's{' '}
                        <br></br>
                        <a href="#" className="text-white font-semibold hover:underline">Terms of Service</a>
                        {' '}and{' '}
                        <a href="#" className="text-white font-semibold hover:underline">Privacy Policy</a>
                        <br></br>
                        <br></br>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;

